import i18next from "i18next";

/**
 * Shared file-upload plumbing for the /admin pages migrated from Retool
 * (EN-5148). Retool's dropzone/file-input widgets validated nothing and its
 * REST queries posted `form-data` with a `binary` part per file; the portal
 * validates before sending and builds the same multipart body by hand.
 */

export interface IFileValidationRules {
  /**
   * Allowed types, as MIME types (`image/png`) and/or extensions (`.csv`).
   * Empty or omitted accepts anything. Extensions are matched
   * case-insensitively — browsers report `.CSV` files with an empty `type`
   * often enough that extension matching is the reliable half.
   */
  accept?: string[];
  /** Hard size cap; the API's own limit should be mirrored here. */
  maxBytes?: number;
}

/** Human-readable size, matching how the limits are usually written (1 MB). */
export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;

  const megabytes = bytes / (1024 * 1024);
  const rounded =
    megabytes >= 10 ? Math.round(megabytes) : Math.round(megabytes * 10) / 10;
  return `${rounded} MB`;
}

/** The `accept` attribute value for a file input, from the same rules. */
export function toAcceptAttribute(accept?: string[]): string | undefined {
  return accept && accept.length > 0 ? accept.join(",") : undefined;
}

function matchesAccept(file: File, accept: string[]): boolean {
  const fileType = file.type.toLowerCase();
  const fileName = file.name.toLowerCase();

  return accept.some((entry) => {
    const rule = entry.trim().toLowerCase();
    if (!rule) return false;
    if (rule.startsWith(".")) return fileName.endsWith(rule);
    if (rule.endsWith("/*")) return fileType.startsWith(rule.slice(0, -1));
    return fileType === rule;
  });
}

/**
 * Validates a picked file against the rules, returning a message to show the
 * user or `null` when it's fine. Client-side validation is a courtesy — the
 * API validates too, and `useFileUpload` surfaces what it says.
 */
export function validateFile(
  file: File,
  rules: IFileValidationRules = {}
): string | null {
  const { accept, maxBytes } = rules;

  if (accept && accept.length > 0 && !matchesAccept(file, accept)) {
    return `Unsupported file type. Choose ${accept.join(" or ")}.`;
  }

  if (maxBytes !== undefined && file.size > maxBytes) {
    return `File is too large (${formatBytes(
      file.size
    )}). The limit is ${formatBytes(maxBytes)}.`;
  }

  if (file.size === 0) {
    return i18next.t("common_fileEmpty");
  }

  return null;
}

export type UploadFormValue =
  | string
  | number
  | boolean
  | File
  | Blob
  | null
  | undefined;

/**
 * Builds the multipart body for an upload. Field names must match the API's
 * `[FromForm]` DTO properties (e.g. `Image` + `TenantId` for the tenant logo,
 * `File` + `WardId` for the patient bulk import) — .NET binds them
 * case-insensitively but the casing in the exports is the safest guide.
 * `null`/`undefined` values are skipped so optional fields aren't sent as the
 * string "undefined".
 */
export function buildUploadFormData(
  fields: Record<string, UploadFormValue>
): FormData {
  const formData = new FormData();

  Object.entries(fields).forEach(([name, value]) => {
    if (value === null || value === undefined) return;

    if (value instanceof File) {
      formData.append(name, value, value.name);
    } else if (value instanceof Blob) {
      formData.append(name, value);
    } else {
      formData.append(name, String(value));
    }
  });

  return formData;
}
